import * as React from 'react';
import { makeObservable } from 'mobx';
import { Button } from '@shared/ui-react/Button';
import { CrossSectionStore, ICrossSectionLoadSelection } from './CrossSectionStore';
import { cs } from '@shared/utils/utils-classname';
import { observer } from 'mobx-react';
import { CrossSectionModelsListComponent } from '@pages/cross-section/shared/CrossSectionModelsListComponent';
import { CrossSectionZoomComponent } from '@pages/cross-section/shared/CrossSectionZoomComponent';
import '../shared/CrossSectionComponent.scss';
import { InputNumber } from 'primereact/inputnumber';


@observer
export class CrossSectionComponent extends React.Component<{
    station?: number; // station for regular cs, NE for free cs
    lat1?: number;
    lon1?: number;
    lat2?: number;
    lon2?: number;
    selection: ICrossSectionLoadSelection;
    treeData?: ITreeData;
    view?: CrossSectionViewState;
    searchDisplacement?: number;
    deltaZFilter?: number;
}> {
    store = new CrossSectionStore(this.props.treeData, this.props.view);

    constructor(props) {
        super(props);
        makeObservable(this);

        if (this.props.searchDisplacement != null) {
            this.store.settings.searchDisplacement = this.props.searchDisplacement;
        }
        if (this.props.deltaZFilter != null) {
            this.store.settings.deltaZFilter = this.props.deltaZFilter;
        }

        if (this.props.lat1 && this.props.lon1) {
            this.store.freeCrossSectionCoords.lat1 = this.props.lat1;
            this.store.freeCrossSectionCoords.lon1 = this.props.lon1;
            this.store.freeCrossSectionCoords.lat2 = this.props.lat2;
            this.store.freeCrossSectionCoords.lon2 = this.props.lon2;
        } else {
            if (this.props.station != null) {
                this.store.settings.station = this.props.station ?? 0;
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.station !== this.props.station && this.props.station !== undefined) {
            this.store.settings.station = this.props.station;
            this.store.fetchDrawing(null, true);
        }
    }

    render() {
        return (
            <div className={cs('cross-section__window', { 'overlay-loading': this.store.isLoading || this.store.isLoadingTree })}>
                {!!this.store.csCanvas &&
                    <form style={{ flex: 0, gap: 5, flexWrap: 'wrap', display: 'inline-flex', margin: '0 0 10px 10px' }} onSubmit={e => {
                        e.preventDefault();
                        this.store.fetchDrawing(null, true)
                    }}>
                        <div id="cs-toolbar-row1" className="flex-row" style={{ flexWrap: 'wrap' }}>
                            <div className="toolbar-group">
                                <Button id="tool-arrow" icon="iki iki-cursor"
                                        tooltip={__('js_map_measuring_tools.clear_and_disable')}
                                        className={!this.store.csCanvas.distanceToolActive && !this.store.csCanvas.areaToolActive ? 'active' : null}
                                        onClick={() => {
                                    this.store.csCanvas.clearAreaTool();
                                    this.store.csCanvas.clearDistanceTool();
                                }}/>

                                <Button icon='iki iki-measure' tooltip={__('js_map_measuring_tools.distance')}
                                        className={this.store.csCanvas.distanceToolActive ? 'active' : null}
                                        onClick={() => this.store.csCanvas.activateDistanceTool()}/>

                                <Button icon='iki iki-area' tooltip={__('js_map_measuring_tools.area')}
                                        className={this.store.csCanvas.areaToolActive ? 'active' : null}
                                        onClick={() => this.store.csCanvas.activateAreaTool()}/>

                                <Button id="clearTools" onClick={this.store.clearAllTools}>{__('cross_section.clear_tools')}</Button>
                            </div>

                            <CrossSectionZoomComponent store={this.store}/>

                            { !this.store.isFreeCrossSection &&
                                <div id="cs-station-controls" className="toolbar-group">
                                    <div id="cs-station-form-group">
                                        <span>{__('tablet.vehicle_station_js')}:</span>
                                        { this.store.latestServerData?.stationEquation &&
                                            <input type="text" style={{ width: 110, fontWeight: 'bold' }}  value={this.store.latestServerData.stationEquation} disabled/>
                                        }
                                        <InputNumber style={{ width: 70 }}
                                               max={999999}
                                               maxFractionDigits={2} useGrouping={false} allowEmpty={false}
                                               value={this.store.settings.station}
                                               onChange={e => this.store.settings.station = e.value}
                                        />
                                    </div>
                                    <div id="cs-step-form-group">
                                        <span>{__('cross_section.step')}:</span>
                                        <InputNumber style={{ width: 30 }} value={this.store.settings.step}
                                                min={1} max={100} step={1} maxFractionDigits={0} useGrouping={false} allowEmpty={false}
                                                onChange={e => this.store.settings.step = e.value}/>
                                    </div>
                                    <div id="cs-station-move-form-group">
                                        <div className="btn-group">
                                            <Button id="back" icon="iki iki-chevron-left" onClick={this.store.stationDecrease} disabled={this.store.isLoading || this.store.settings.station <= 0}/>
                                            <Button id="forward" icon="iki iki-chevron-right" onClick={this.store.stationIncrease} disabled={this.store.isLoading}/>
                                        </div>
                                    </div>
                                </div>
                            }

                            <Button className="cross-section__more-toolbar_toggle"
                                    icon={this.store.showMobileMoreToolbar ? 'iki iki-chevron-up' : 'iki iki-chevron-down'}
                                    onClick={() => this.store.showMobileMoreToolbar = !this.store.showMobileMoreToolbar}>{__('js_map_measuring_tools.more')}</Button>
                        </div>

                        <div id="cs-toolbar-row2" className={cs("flex-row gap-half toolbar-group", { open: this.store.showMobileMoreToolbar })} style={{ flexWrap: 'wrap' }}>
                            <Button className='cs-pdf-btn' icon='iki iki-file' onClick={this.store.createPdf} loading={this.store.isCreatingPdf} style={{ marginRight: 10}}>PDF</Button>

                            <div className="flex-row">
                                <label htmlFor="crossSectionWidthInput">{__('cross_section.width')}:</label>
                                <InputNumber id="crossSectionWidthInput" style={{ width: 60 }}
                                       min={1} max={1000} step={1} maxFractionDigits={0} useGrouping={false} allowEmpty={false}
                                       value={this.store.csCanvas.widthMetersInitial}
                                       onChange={e => this.store.csCanvas.widthMetersInitial =e.value}
                                       disabled={this.store.isFreeCrossSection}
                                />
                            </div>

                            <div className="flex-row">
                                <label htmlFor="logpointDelta">{__('cross_section.logpoint_search_displacement')}:</label>
                                <InputNumber id="logpointDelta" min={0} step={0.1} maxFractionDigits={1} minFractionDigits={1}
                                             useGrouping={false} allowEmpty={false} style={{ width: 60 }}
                                       value={this.store.settings.searchDisplacement} onChange={e => this.store.settings.searchDisplacement = e.value}/>
                            </div>

                            { !this.store.isFreeCrossSection &&
                                <div className="flex-row">
                                    <label htmlFor="deltaZFilter">{__('cross_section.logpoint_dz_filter')} ±:</label>
                                    <InputNumber id="deltaZFilter" min={0} step={0.1} maxFractionDigits={2} minFractionDigits={2}
                                                 useGrouping={false} allowEmpty={false} style={{ width: 70 }}
                                           value={this.store.settings.deltaZFilter} onChange={e => this.store.settings.deltaZFilter = e.value}/>
                                </div>
                            }

                            <button type="submit" disabled={this.store.isLoading}>{__('general.apply')}</button>
                        </div>
                    </form>
                }

                { !this.store.isLoadingTree &&
                    <div className={cs('cross-section__models_canvas_wrapper', { 'show-models': this.store.showMobileModelsList })}>
                        <Button primary className="cross-section__models_toggle"
                                icon={this.store.showMobileModelsList ? 'iki iki-chevron-down' : 'iki iki-chevron-up'}
                                onClick={() => this.store.showMobileModelsList = !this.store.showMobileModelsList}>{__('general.models')}</Button>

                        <CrossSectionModelsListComponent store={this.store} hasPipes={true} hasLayers={true}/>

                        <div className="cross-section__canvas_wrapper">
                            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }} ref={el => this.store.setCanvasEl(el, this.props.selection)}></div>
                        </div>
                    </div>
                }
            </div>
        );
    }
}
